import Vue from 'vue';
import Component from 'vue-class-component';
import { Emit, Inject, Prop, Provide, Ref, Watch } from 'vue-property-decorator';

import { Placeholder, ScheduleClass, ScheduleResponseDto } from '@/tools/models/class-builder.models';
import { AssignPlaceholdersToBlocksParams, CreatePlaceholderParams, ScheduleEntrySelected, UpdatePlaceholderParams } from '@/tools/params/class-builder.params';
import { PlaceholderService } from '@/tools/services/placeholder.service';
import { ClassesService } from '@/classes/services/classes.service';
import { HolidayService } from '@/tools/services/holiday.service';
import { WORKING_DAYS } from '@/tools/values/schedule.values';
import { ClassScheduleProvider } from '../class-schedule.provider';
import KEYS from '@/values/keys';
import { ScheduleService } from '@/tools/services/schedule.service';
import { StaffService } from '@/tools/services/staff.service';
import EdClassBuilderRemovePlaceholder from '../ed-class-builder-remove-placeholder/ed-class-builder-remove-placeholder.component.vue';
import EdClassSubjectPlaceholderCreateModal from '../ed-class-subject-placeholder-create-modal/ed-class-subject-placeholder-create-modal.component.vue';
import EdClassSubjectPlaceholderToBlockModal from '../ed-class-subject-placeholder-to-block-modal/ed-class-subject-placeholder-to-block-modal.component.vue';
import { ScheduleTypeEnum } from '@/tools/enums/schedule-type.enum';
import { EdGenericCalendarWeekdays } from '@/core/components/ed-generic-calendar/interfaces/ed-generic-calendar-weekdays.interface';
import { ScheduleClassResponse } from '@/tools/interfaces/schedule-class-response.interface';
import Moment from 'moment';
import { _CLASSES_STORE } from '@/classes/values/classes.values';

@Component({
  name: 'ed-class-builder',
  components: {
    EdClassBuilderSchedule: () => import(/* "EdClassBuilderSchedule" */ '../ed-class-builder-schedule/ed-class-builder-schedule.component.vue'),
    EdClassBuilderSidebar: () => import(/* "EdClassBuilderSidebar" */ '../ed-class-builder-sidebar/ed-class-builder-sidebar.component.vue'),
    EdClassScheduleHeader: () => import(/* "EdClassScheduleHeader" */ '../ed-class-schedule-header/ed-class-schedule-header.component.vue')
  }
})
export default class EdClassBuilderComponent extends Vue {
  @Prop({ required: true }) readonly classId: number;
  @Prop({ required: true }) readonly organizationId: number;

  @Ref('scheduleBuilder')  
  scheduleBuilder: any;

  @Provide()
  ScheduleProvider = new ClassScheduleProvider();

  loading: boolean = true;

  @Emit('onBack')
  emitOnBack() {
    return;
  }

  @Emit('onSeePlaceholderSelectedOnCalendar')
  emitOnSeePlaceholderSelectedOnCalendar(placeholder: Placeholder): Placeholder {
    return placeholder;
  }

  schedule: ScheduleResponseDto[] = [];
  hasBackToAsignPlaceholder = false;
  scheduleEntries: ScheduleResponseDto[] = [];
  blocksChecked: ScheduleEntrySelected[] = [];
  blocksToAssign: ScheduleEntrySelected[] = [];
  blockSelected: ScheduleEntrySelected = null;
  placeholders: Placeholder[] = [];
  placeholderSelected: Placeholder = null;
  className: string = null;
  placeholderToRemove: Placeholder = null;
  baseAllowTeachersToEdit = false;
  allowTeachersToEdit = false;
  isSidebarOpen = true;
  isCalendarExpired : boolean = false;

  get weekdays(): number[] | EdGenericCalendarWeekdays[] {
    if (!this.isWeeklySchedule) {
      return this.ScheduleProvider.weekdays.map(day => ({
        ...day,
        label: this.$t(`tools.classBuilder.${day.key}`).toString(),
      }));
    }

    return this.ScheduleProvider.weekdays;
  }

  get isWeeklySchedule(): boolean {
    return this.ScheduleProvider.isWeeklySchedule;
  }

  get subjects() {
    return this.ScheduleProvider.subjects;
  }

  get teachers() {
    return this.ScheduleProvider.teachers;
  }

  get isAbleToModify(): boolean {
    if(this.isCalendarExpired) 
      return false;
    const alwaysAble = ['SA', 'SDA'].includes(this.$store.state.AuthStore.user?.role);
    return alwaysAble || (this.baseAllowTeachersToEdit && this.$store.state.AuthStore.user?.role === 'TEACHER');
  }

  get isZoomInDisabled(): boolean {
    return this.ScheduleProvider.calendarZoomInDisabled;
  }

  get isZoomOutDisabled(): boolean {
    return this.ScheduleProvider.calendarZoomOutDisabled;
  }

  async created() {
    await this.fetchInitData();
    this.$store.commit(_CLASSES_STORE.MUTATIONS.SET_CLASS_SCHEDULE_M, { isAbleToEdit: this.isAbleToModify });
  }

  async fetchInitData() {
    try {
      this.loading = true;
      await this.refreshSchedule();
      
      const classDataPromise = await ClassesService.getClass(this.$route.params.classId);
      const workdaysPromise = HolidayService.getWorkingDaysByClassId(this.$route.params.classId);
      const [classData, workdays] = await Promise.all([
        classDataPromise,
        workdaysPromise
      ]);
  
      this.setWeekdays(classData, workdays);
      this.className = classData.name;
      this.allowTeachersToEdit = classData.allowTeachersModifySchedule;
      this.baseAllowTeachersToEdit = classData.allowTeachersModifySchedule;
      this.isCalendarExpired = Moment(classData.schoolDay?.endDate).isBefore(Moment());

      this.ScheduleProvider.scheduleStartHour = classData.startScheduleTime;
      this.ScheduleProvider.scheduleEndHour = classData.endScheduleTime;
      this.ScheduleProvider.classId = this.classId;
  
      await this.setSubjects(this.ScheduleProvider.classId);
      await this.setTeachers();
    } catch(err) {
      this.$EdModal.createMessageModal({
        title: this.$i18n.t('courses.errors.errorTitle'),
        body: this.$i18n.t('courses.errors.errorSavingObject'),
        type: KEYS.EVENT_TYPES.DANGER
      });
    } finally {
      this.loading = false;
    }
  }

  setWeekdays(classData: ScheduleClassResponse, workdays: WORKING_DAYS[]): void {
    if (classData.scheduleType === ScheduleTypeEnum.AB) {
      if (classData.scheduleShortDay) {
        this.ScheduleProvider.setScheduleShortDay(classData.scheduleShortDay);
      } else {
        this.ScheduleProvider.setABWeekdays();
      }
    } else {
      this.ScheduleProvider.setWeekdays(workdays);
    }
  }

  async refreshSchedule() {
    this.schedule = await ScheduleService.getScheduleByClassId(this.classId);
    this.scheduleEntries = this.schedule.map(a => a);
    const placeholders = await PlaceholderService.getPlaceholders(
      this.$route.params.classId,
      true
    );

    this.ScheduleProvider.subjectsAdded = placeholders;
  }

  async setSubjects(classId: number) {
    const subjects = await ClassesService.getCoursesByClassId(classId);

    this.ScheduleProvider.subjects = subjects.map(subject => ({
      key: subject.id,
      value: subject.title
    }))
  }

  async setTeachers() {
    const { content } = await StaffService.getUsersByOrganizationId(
      this.organizationId, 0, 100, 'TEACHER,SA,SDA'
    );

    this.ScheduleProvider.teachers = content
      .filter(teacher => teacher.status === 'APPROVED')
      .map(teacher => ({
        key: teacher.personId,
        value: teacher?.fullName || ''
      }));
  }

  openCreatePlaceholderModal(hasBack: boolean = false) {
    this.hasBackToAsignPlaceholder = hasBack;
    this.placeholderSelected = null;
    
    this.$nextTick(() => {
      this.$EdModal.createModal(EdClassSubjectPlaceholderCreateModal, {
        onCreatePlaceholder: this.onCreatePlaceholder,
        onClose: this.onCloseCreatePlaceholderModal,
        placeholder: this.placeholderSelected,
        subjects: this.subjects,
        teachers: this.teachers
      });
    });
  }

  onCloseCreatePlaceholderModal() {
    if (this.hasBackToAsignPlaceholder) {
      setTimeout(() => {
        this.$EdModal.createModal(EdClassSubjectPlaceholderToBlockModal, {
          placeholders: this.ScheduleProvider.subjectsAdded,
          blocksToAssign: this.blocksToAssign,
          blocksChecked: this.blocksChecked,
          blockToAssign: this.blockSelected,
          cache: true,
          onGoToAddPlaceholders: this.openCreatePlaceholderModal,
          onAddPlaceholdersSelected: this.onAssignPlaceholdersToBlock,
          isWeeklySchedule: this.isWeeklySchedule
        })
        this.hasBackToAsignPlaceholder = false;
      }, 400)
    }
  }

  onCreatePlaceholder(data: CreatePlaceholderParams | UpdatePlaceholderParams) {
    if (this.placeholderSelected) {
      this.updatePlaceholder(data as UpdatePlaceholderParams);
    } else {
      this.createPlaceholder(data);
    }
  }

  async createPlaceholder(data: CreatePlaceholderParams) {
    try {
      this.loading = true;
    
      const quickAddSubjectedCreated = await PlaceholderService.addPlaceholder(this.ScheduleProvider.classId, data);
      this.ScheduleProvider.subjectsAdded.push(quickAddSubjectedCreated);

      if (this.hasBackToAsignPlaceholder) {
        this.$EdModal.createModal(EdClassSubjectPlaceholderToBlockModal, {
          placeholders: this.ScheduleProvider.subjectsAdded,
          blocksToAssign: this.blocksToAssign,
          blocksChecked: this.blocksChecked,
          blockToAssign: this.blockSelected,
          cache: true,
          onGoToAddPlaceholders: this.openCreatePlaceholderModal,
          onAddPlaceholdersSelected: this.onAssignPlaceholdersToBlock,
          isWeeklySchedule: this.isWeeklySchedule
        })

        this.hasBackToAsignPlaceholder = false;
      }
    } catch(err) {
      this.$EdModal.createMessageModal({
        title: this.$i18n.t('courses.errors.errorTitle'),
        body: this.$i18n.t('courses.errors.errorSavingObject'),
        type: KEYS.EVENT_TYPES.DANGER
      });
    } finally {
      this.loading = false;
    }
  }

  onOpenModalEditPlaceholder(placeholder: Placeholder) {
    this.placeholderSelected = placeholder;

    setTimeout(() => {
      this.$EdModal.createModal(EdClassSubjectPlaceholderCreateModal, {
        onCreatePlaceholder: this.onCreatePlaceholder,
        onClose: this.onCloseCreatePlaceholderModal,
        placeholder: this.placeholderSelected,
        subjects: this.subjects,
        teachers: this.teachers
      });
    }, 100);
  }

  async updatePlaceholder(data: UpdatePlaceholderParams) {
    try {
      this.loading = true;
      await PlaceholderService.updatePlaceholder(this.classId, data.id, data);
      this.refreshSchedule();
    } catch(error) {
      this.$EdModal.createMessageModal({
        title: this.$i18n.t('courses.errors.errorTitle'),
        body: this.$i18n.t('courses.errors.errorSavingObject'),
        type: KEYS.EVENT_TYPES.DANGER
      });
    } finally {
      this.loading = false;
    }
  }

  handleDeletePlaceholder(placeholder: Placeholder) {
    this.placeholderToRemove = placeholder;
    this.$EdModal.createModal(EdClassBuilderRemovePlaceholder, {
      placeholder: this.placeholderToRemove,
      onRemoveBlock: this.onDeletePlaceholder
    })

  }

  async onDeletePlaceholder(placeholder: Placeholder) {
    try {
      this.loading = true;
      await this.ScheduleProvider.removeSubject(
        placeholder.id
      );
      await this.refreshSchedule();
      this.placeholderToRemove = null;
    } catch(error) {
      this.$EdModal.createMessageModal({
        title: this.$i18n.t('courses.errors.errorTitle'),
        body: this.$i18n.t('courses.errors.errorSavingObject'),
        type: KEYS.EVENT_TYPES.DANGER
      });
    } finally {
      this. loading = false;
    }
  }

  removePlaceholderFromSchedule(
    placeholder: Placeholder,
    scheduleClass: ScheduleClass[],
  ): ScheduleClass[] {
    
    return scheduleClass.map((sc) => {
      sc.entries = sc.entries.map((se) => {
        se.placeholders = se.placeholders.filter((ph) => ph.id !== placeholder.id);

        return se;
      });

      return sc;
    });
  }

  removePlaceholderFromList(
    placeholder: Placeholder, 
    placeholdersList: Placeholder[]
  ): Placeholder[] {
    return placeholdersList.filter((ph) => ph.id !== placeholder.id);
  }

  onToggleSelectBlock(data: ScheduleEntrySelected) {
    this.blocksChecked = this.getBlocksCheckedUpdated(data, this.blocksChecked);
  }

  getBlocksCheckedUpdated(data: ScheduleEntrySelected, blocksChecked: ScheduleEntrySelected[]): ScheduleEntrySelected[] {
    if (data.id) {
      const index = blocksChecked.findIndex((block) => block.id === data.id);

      if (index !== -1) {
        blocksChecked.splice(index, 1);
      } else {
        blocksChecked.push(data);
      }
    }

    return blocksChecked;
  }

  openAssignPlaceholdersToBlockModal(data: ScheduleEntrySelected) {
    const blockToAssign: ScheduleEntrySelected[] = [];

    this.blockSelected = data;
    this.scheduleEntries.forEach(se => {
      se.entries.forEach(entry => {
        if (entry.scheduleBlock.id === data.scheduleBlockId) {
          blockToAssign.push({
            id: entry.id,
            scheduleBlockId: entry.scheduleBlock.id,
            workingDay: entry.workingDay,
          });
        }
      })
    });

    this.blocksToAssign = blockToAssign;
    this.$EdModal.createModal(EdClassSubjectPlaceholderToBlockModal, {
      placeholders: this.ScheduleProvider.subjectsAdded,
      blocksToAssign: this.blocksToAssign,
      blocksChecked: this.blocksChecked,
      blockToAssign: this.blockSelected,
      cache: false,
      onGoToAddPlaceholders: this.openCreatePlaceholderModal,
      onAddPlaceholdersSelected: this.onAssignPlaceholdersToBlock,
      isWeeklySchedule: this.isWeeklySchedule
    })
  }

  async onAssignPlaceholdersToBlock(data: AssignPlaceholdersToBlocksParams) {
    this.loading = true;

    const placeholderIds = data.placeholders.map((ph) => ph.id);
    const unrelatePlaceholderIds = data.unrelatedPlaceholders.map((ph) => ph.id);
    const blockIds = data.blocks.map((block) => block.id);
    await PlaceholderService.relatePlaceholder(this.$route.params.classId, blockIds, placeholderIds)
    await PlaceholderService.unrelatePlaceholder(this.$route.params.classId, blockIds, unrelatePlaceholderIds)
    this.refreshSchedule();
    this.blocksChecked = [];
    this.scheduleBuilder?.resetSelection();

    this.loading = false;
  }

  getScheduleEntriesUpdated(scheduleEntries: ScheduleClass[], { placeholders, blocks }: AssignPlaceholdersToBlocksParams) {
    return scheduleEntries.map((scheduleClass) => {
      scheduleClass.entries = scheduleClass.entries.map((entry) => {
        const entrySelected = blocks.find((entrySelected) => entrySelected.id === entry.id);

        if (entrySelected) {
          entry.placeholders = placeholders;
        }

        return entry;
      });

      return scheduleClass;
    });
  }

  async saveClass(allowTeachersToEdit: boolean) {
    try {
      this.loading = true;
      this.allowTeachersToEdit = allowTeachersToEdit;

      if (this.allowTeachersToEdit && !this.baseAllowTeachersToEdit) {
        await ClassesService.allowTeachersToEdit(this.$route.params.classId);
      } else if (!this.allowTeachersToEdit && this.baseAllowTeachersToEdit) {
        await ClassesService.disallowTeachersToEdit(this.$route.params.classId);
      }
      this.emitOnBack();
    } catch(error) {
      this.$EdModal.createMessageModal({
        title: this.$i18n.t('courses.errors.errorTitle'),
        body: this.$i18n.t('courses.errors.errorSavingObject'),
        type: KEYS.EVENT_TYPES.DANGER
      });
    } finally {
      this.loading = false;
    }
  }

  toggleSidebar(val) {
    this.isSidebarOpen = val;
  }

  @Watch('schedule')
  onScheduleChange() {
    this.scheduleEntries = this.schedule.map(a => a);
  }

  onPlusEmit() {
    this.scheduleBuilder?.zoomIn();
  }

  onMinusEmit() {
    this.scheduleBuilder?.zoomOut();
  }
}